import axios from "axios";
import router from "@/router";
import store from "@/store";
import { showAlertModal } from "@/utils";
import { jwtDecode } from "jwt-decode";
import { i18n } from "./i18n";

axios.defaults.baseURL =
    process.env.VUE_APP_API_URL || "http://localhost:8000/api";

export function willExpireSoon(token, offset = 30) {
    if (!token) return true;
    try {
        const { exp } = jwtDecode(token); // epoch seconds
        return exp * 1000 - Date.now() < offset * 1000;
    } catch {
        return true; // malformed token
    }
}

let isRefreshing = false;
let waiting = [];
const SESSION_CANCEL = "session-expired";

function enqueue(cb) {
    waiting.push(cb);
}
function flush(newToken) {
    waiting.forEach((cb) => cb(newToken));
    waiting = [];
}

export async function refreshToken(refresh) {
    if (isRefreshing) {
        // someone else is doing it
        return new Promise((resolve) => enqueue(resolve));
    }
    isRefreshing = true;

    try {
        const { data } = await axios.post(
            `${axios.defaults.baseURL}/auth/token/refresh/`,
            { refresh }
        );
        localStorage.setItem("accessToken", data.access);
        if (data.refresh) localStorage.setItem("refreshToken", data.refresh);
        axios.defaults.headers.common.Authorization = `Bearer ${data.access}`;
        flush(data.access);
        return data.access;
    } catch (e) {
        flush(null);
        throw e;
    } finally {
        isRefreshing = false;
    }
}

let loggingOut = false; // ← new global flag

export function hardLogout(reason = "tokenExpired") {
    if (loggingOut) return; // run exactly once
    loggingOut = true;

    showAlertModal(store, i18n.global.t(reason), "warning", 8000);

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    store.commit("auth/LOGOUT");
    delete axios.defaults.headers.common.Authorization;

    router.push({
        name: "Login",
        query: { next: router.currentRoute.value.fullPath },
    });
}

export function clearLogoutFlag() {
    loggingOut = false;
}

axios.interceptors.response.use(
    (res) => res,
    (err) => {
        if (axios.isCancel(err) && err.message === SESSION_CANCEL) {
            /* Resolve with a sentinel so awaits don’t throw
         and .catch() blocks never run.           */
            return Promise.resolve({ __sessionCancelled: true });
        }
        return Promise.reject(err); // real errors continue bubbling
    }
);

// swallow every Axios Cancel emitted while we’re logging-out
axios.interceptors.response.use(null, (err) => {
    if (axios.isCancel(err)) {
        /* do nothing – pretend the call never happened */
        return Promise.resolve(); // caller sees a resolved promise
    }
    return Promise.reject(err); // real errors keep bubbling
});

// ────────────────────────────────────────────────────────────────────────────
//  Attach Authorization header to every outgoing request (unchanged)
// ────────────────────────────────────────────────────────────────────────────
axios.interceptors.request.use(async (cfg) => {
    if (loggingOut) {
        // Cancel all in-flight requests quietly while we redirect
        return Promise.reject(new axios.Cancel(SESSION_CANCEL));
    }

    if (cfg.url?.includes("/auth/token/refresh/")) return cfg;

    let token = localStorage.getItem("accessToken");
    const refresh = localStorage.getItem("refreshToken");

    if (token && willExpireSoon(token) && refresh) {
        try {
            token = await refreshToken(refresh); // <── proactive refresh
        } catch {
            hardLogout();
        }
    }

    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

// ────────────────────────────────────────────────────────────────────────────
//  Intercept 401s and refresh the access token transparently
// ────────────────────────────────────────────────────────────────────────────
axios.interceptors.response.use(
    (res) => res,
    async (err) => {
        if (err.config?.url?.includes("/auth/token/refresh/")) {
            hardLogout();
            return Promise.reject(new axios.Cancel(SESSION_CANCEL));
        }

        const { response, config } = err;
        if (
            response?.status !== 401 ||
            response.data?.code !== "token_not_valid" ||
            config._retry
        ) {
            return Promise.reject(err); // unrelated error
        }
        config._retry = true;

        try {
            const newToken = await refreshToken(
                localStorage.getItem("refreshToken")
            );
            if (!newToken) throw err;
            config.headers.Authorization = `Bearer ${newToken}`;
            return axios(config);
        } catch (e) {
            hardLogout(); // triggers flag
            return Promise.reject(new axios.Cancel(SESSION_CANCEL));
        }
    }
);

/**
 * Fetch cities for multiple countries at once.
 * @param {Array<number|string>} countryIds - An array of country IDs.
 * @param lang
 * @returns {Promise<Object>} - A lookup object { countryId: { cityId: cityName } }
 */
export const getCityLookupBatchByCountry = async (
    countryIds = [],
    lang = "en",
    search = "",
    cityIds = []
) => {
    const cacheKey = `cityLookupByCountry_${lang}`;
    let cached = sessionStorage.getItem(cacheKey);
    let cityCache = cached ? JSON.parse(cached) : {};

    // Determine what cityIds are already cached
    const missingCityIds = new Set();
    cityIds.forEach((cityId) => {
        const found = Object.values(cityCache).some((cities) =>
            cities.hasOwnProperty(cityId)
        );
        if (!found) missingCityIds.add(cityId);
    });

    // If all cityIds are cached and there's no search term, skip API call
    if (!missingCityIds.size && !search) return cityCache;

    try {
        const params = new URLSearchParams();
        countryIds.forEach((id) => params.append("countries", id));
        [...missingCityIds].forEach((id) => params.append("city_ids", id));
        params.append("lang", lang);
        if (search) params.append("search", search);

        const response = await api.get("combined-location/", { params });
        const cities = response.data?.cities || [];

        // ─── LRU (least recently used) CACHE (max 3 countries) ────────────────────────────
        const orderKey = `cityLookupOrder_${lang}`;
        // load existing LRU order
        let order = JSON.parse(sessionStorage.getItem(orderKey)) || [];

        cities.forEach((loc) => {
            const countryId = String(loc.country_id);

            // 1) remove old occurrence
            order = order.filter((id) => id !== countryId);

            // 2) merge into cache
            if (!cityCache[countryId]) cityCache[countryId] = {};
            cityCache[countryId][loc.id] = loc.name;

            // 3) bump to most-recent
            order.push(countryId);
        });

        // 4) evict until only 3 remain
        while (order.length > 3) {
            const oldest = order.shift();
            delete cityCache[oldest];
        }

        // 5) persist cache + order
        sessionStorage.setItem(cacheKey, JSON.stringify(cityCache));
        sessionStorage.setItem(orderKey, JSON.stringify(order));
        return cityCache;
    } catch (error) {
        console.error("Error fetching city lookup:", error);
        return cityCache; // fallback to what you have
    }
};

/**
 * Fetches a list of all available countries from the API.
 *
 * @async
 * @function getCountries
 * @param {string} lang - Language code (e.g., "en", "fr", "ro").
 * @returns {Promise<Array>} - A promise that resolves to an array of country objects.
 */
export const getCountries = async (lang = "en") => {
    try {
        const response = await api.get("/countries/", { params: { lang } });
        return response.data;
    } catch (error) {
        console.error("Error fetching countries:", error);
        return [];
    }
};

const api = axios;

export default api;
